import { generateId } from "../functions/generate-firestore-id.function";
import { CoreConfigData } from "../models/core-config-data.model";
import { FixedLengthArray } from "../types/fixed-length-array.type";
import { FerryRotoMachineChannels, ReolinkCameraMachineChannels } from "./ferry-roto-machine-channels.config";


///////////////////////////////////////////////////
//////////////////// Devices //////////////////////
///////////////////////////////////////////////////


export type Device =
    Productivity1000 |
    DX200 |
    DuraPulse |
    SureServo2 |
    IoLinkMaster_54631 |
    YRC1000 |
    FerryRotoMachine |
    ReolinkCamera |
    TLB4ModbusTCP |
    CR011R;

// don't export since users shouldn't be able to directly create a device
export interface BaseDevice extends CoreConfigData {
    deviceType: DeviceType;
    ipAddress: string;
    enabled: boolean;
}

export enum DeviceType {
    Productivity1000 = "Productivity1000", // PLC
    DX200 = "DX200", // Robot Controller
    DuraPulse = "DuraPulse", // Modbus VFD
    SureServo2 = "SureServo2", // Modbus Servo Drive
    IoLinkMaster_54631 = "IoLinkMaster_54631", // IoLink Master
    YRC1000 = "YRC1000", // Robot Controller
    FerryRotoMachine = "FerryRotoMachine", // Ferry Roto Machine
    ReolinkCamera = "ReolinkCamera",
    TLB4ModbusTCP = "TLB4ModbusTCP",
    CR011R = "CR011R", // DMX WS2812B Light Controller
}



export interface Productivity1000 extends BaseDevice {
    cards: Productivity1000Card[];
    deviceType: DeviceType.Productivity1000;
}

export interface DX200 extends BaseDevice {
    channels: DX200Channels;
    deviceType: DeviceType.DX200;
}

export interface YRC1000 extends BaseDevice {
    channels: YRC1000Channels;
    userChannels: YRC1000UserChannels;
    deviceType: DeviceType.YRC1000;
}




export enum DurapulseStateCommand {
    Run = "Run",
    Stop = "Stop",
    EnableJog = "Enable Jog",
}

export enum DurapulseDirectionCommand {
    Forward = "Forward",
    Reverse = "Reverse",
}

export enum DurapulseStopMethod {
    RAMP_TO_STOP = "Ramp to Stop",
    COAST_TO_STOP = "Coast to Stop",
}

export enum DurapulseStateStatus {
    Run = "Run",
    Standby = "Standby",
    DecelDuringStop = "Decel during stop",
    Stop = "Stop",
}

export enum DurapulseDirectionStatus {
    Forward = "Forward",
    Reverse = "Reverse",
    TransitionFromReverseToForward = "Transition from Reverse to Forward",
    TransitionFromForwardToReverse = "Transition from Forward to Reverse",
}

type DX200Channels = {
    alarm_reset_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Alarm Reset Command",
        machineName: "alarm_reset_cmd",
        description: "Sends a command to reset any alarms that are active",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    start_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Start Command",
        machineName: "start_cmd",
        description: "Sends a command to start the robot",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    servo_on_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Servo On Command",
        machineName: "servo_on_cmd",
        description: "Sends a command to turn the servo on",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    servo_off_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Servo Off Command",
        machineName: "servo_off_cmd",
        description: "Sends a command to turn the servo off",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    hold_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Hold Command",
        machineName: "hold_cmd",
        description: "Sends a command to hold the robot",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    continue_cycle_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Continue Cycle Command",
        machineName: "continue_cycle_cmd",
        description: "Sends a command to continue the robot's cycle",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    call_master_job_cmd: ChannelOverride<DX200_DigitalOutput, {
        displayName: "Call Master Job Command",
        machineName: "call_master_job_cmd",
        description: "Sends a command to call the master job",
        units: null,
        channelType: DX200ChannelType.DigitalOutput,
    }>,
    program_number: ChannelOverride<DX200_AnalogOutput, {
        displayName: "Program Number",
        machineName: "program_number",
        description: "Sets the program number to run",
        units: null,
        channelType: DX200ChannelType.AnalogOutput,
    }>,

    error: ChannelOverride<DX200_DigitalInput, {
        displayName: "Error",
        machineName: "error",
        description: "Indicates whether an error has occurred",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    pendant_estop_ok: ChannelOverride<DX200_DigitalInput, {
        displayName: "Pendant E-Stop OK",
        machineName: "pendant_estop_ok",
        description: "Indicates whether the pendant E-stop is OK",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    major_alarm: ChannelOverride<DX200_DigitalInput, {
        displayName: "Major Alarm",
        machineName: "major_alarm",
        description: "Indicates whether a major alarm has occurred",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    top_master_job: ChannelOverride<DX200_DigitalInput, {
        displayName: "Top Master Job",
        machineName: "top_master_job",
        description: "Indicates that the program execution is at the top of the master job",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    remote_mode: ChannelOverride<DX200_DigitalInput, {
        displayName: "Remote Mode",
        machineName: "remote_mode",
        description: "Indicates whether the robot is in remote mode",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    teach_mode: ChannelOverride<DX200_DigitalInput, {
        displayName: "Teach Mode",
        machineName: "teach_mode",
        description: "Indicates whether the robot is in teach mode",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    play_mode: ChannelOverride<DX200_DigitalInput, {
        displayName: "Play Mode",
        machineName: "play_mode",
        description: "Indicates whether the robot is in play mode",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    servo_on: ChannelOverride<DX200_DigitalInput, {
        displayName: "Servo On",
        machineName: "servo_on",
        description: "Indicates whether the servo is on",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    holding: ChannelOverride<DX200_DigitalInput, {
        displayName: "Holding",
        machineName: "holding",
        description: "Indicates whether the robot is holding",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,
    running: ChannelOverride<DX200_DigitalInput, {
        displayName: "Running",
        machineName: "running",
        description: "Indicates whether the robot is running",
        units: null,
        channelType: DX200ChannelType.DigitalInput,
    }>,



}

type YRC1000Channels = {
    alarm_reset_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Alarm Reset Command",
        machineName: "alarm_reset_cmd",
        description: "Sends a command to reset any alarms that are active",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    start_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Start Command",
        machineName: "start_cmd",
        description: "Sends a command to start the robot",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    servo_on_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Servo On Command",
        machineName: "servo_on_cmd",
        description: "Sends a command to turn the servo on",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    servo_off_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Servo Off Command",
        machineName: "servo_off_cmd",
        description: "Sends a command to turn the servo off",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    hold_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Hold Command",
        machineName: "hold_cmd",
        description: "Sends a command to hold the robot",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    continue_cycle_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Continue Cycle Command",
        machineName: "continue_cycle_cmd",
        description: "Sends a command to continue the robot's cycle",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    call_master_job_cmd: ChannelOverride<YRC1000_DigitalOutput, {
        displayName: "Call Master Job Command",
        machineName: "call_master_job_cmd",
        description: "Sends a command to call the master job",
        units: null,
        channelType: YRC1000ChannelType.DigitalOutput,
    }>,
    program_number: ChannelOverride<YRC1000_AnalogOutput, {
        displayName: "Program Number",
        machineName: "program_number",
        description: "Sets the program number to run",
        units: null,
        channelType: YRC1000ChannelType.AnalogOutput,
    }>,

    error: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Error",
        machineName: "error",
        description: "Indicates whether an error has occurred",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    pendant_estop_ok: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Pendant E-Stop OK",
        machineName: "pendant_estop_ok",
        description: "Indicates whether the pendant E-stop is OK",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    major_alarm: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Major Alarm",
        machineName: "major_alarm",
        description: "Indicates whether a major alarm has occurred",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    top_master_job: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Top Master Job",
        machineName: "top_master_job",
        description: "Indicates that the program execution is at the top of the master job",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    remote_mode: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Remote Mode",
        machineName: "remote_mode",
        description: "Indicates whether the robot is in remote mode",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    teach_mode: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Teach Mode",
        machineName: "teach_mode",
        description: "Indicates whether the robot is in teach mode",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    play_mode: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Play Mode",
        machineName: "play_mode",
        description: "Indicates whether the robot is in play mode",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    servo_on: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Servo On",
        machineName: "servo_on",
        description: "Indicates whether the servo is on",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    holding: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Holding",
        machineName: "holding",
        description: "Indicates whether the robot is holding",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    running: ChannelOverride<YRC1000_DigitalInput, {
        displayName: "Running",
        machineName: "running",
        description: "Indicates whether the robot is running",
        units: null,
        channelType: YRC1000ChannelType.DigitalInput,
    }>,
    xPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "xPos",
        machineName: "xPos",
        description: "The current x position of the robot",
        units: "mm",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    yPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "yPos",
        machineName: "yPos",
        description: "The current y position of the robot",
        units: "mm",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    zPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "zPos",
        machineName: "zPos",
        description: "The current z position of the robot",
        units: "mm",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    aPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "aPos",
        machineName: "aPos",
        description: "The current a rotation of the robot",
        units: "deg",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    bPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "bPos",
        machineName: "bPos",
        description: "The current b rotation of the robot",
        units: "deg",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    cPos: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "cPos",
        machineName: "cPos",
        description: "The current c rotation of the robot",
        units: "deg",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
    speed: ChannelOverride<YRC1000_AnalogInput, {
        displayName: "Speed",
        machineName: "speed",
        description: "The current speed of the robot",
        units: "mm/s",
        channelType: YRC1000ChannelType.AnalogInput,
    }>,
}




type DuraPulseChannels = {

    /// Command Channels
    state_cmd: ChannelOverride<Durapulse_EnumOutput<DurapulseStateCommand>, {
        displayName: "State Command",
        machineName: "state_cmd",
        description: "Sets the VFD state",
        units: null,
        channelType: DurapulseChannelType.EnumOutput,
        initialValue: DurapulseStateCommand.Stop,
    }>,
    direction_cmd: ChannelOverride<Durapulse_EnumOutput<DurapulseDirectionCommand>, {
        displayName: "Direction Command",
        machineName: "direction_cmd",
        description: "Sets the VFD direction",
        units: null,
        channelType: DurapulseChannelType.EnumOutput,
        initialValue: DurapulseDirectionCommand.Forward,
    }>,
    frequency_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Frequency Command",
        machineName: "frequency_cmd",
        description: "Sets the VFD speed in Hz",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogOutput,
        initialValue: 0,
    }>,
    acceleration_time_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Acceleration Time Command",
        machineName: "acceleration_time_cmd",
        description: "Sets the acceleration time of the VFD as defined by the time it takes to go from 0 to max speed.",
        units: "ms",
        channelType: DurapulseChannelType.AnalogOutput,
        initialValue: 5000,
    }>,
    stop_method_cmd: ChannelOverride<Durapulse_EnumOutput<DurapulseStopMethod>, {
        displayName: "Stop Method Command",
        machineName: "stop_method_cmd",
        description: "Sets the method that the VFD will use to stop",
        units: null,
        channelType: DurapulseChannelType.EnumOutput,
        initialValue: DurapulseStopMethod.RAMP_TO_STOP,
    }>,
    reset_cmd: ChannelOverride<Durapulse_DigitalOutput, {
        displayName: "Reset Command",
        machineName: "reset_cmd",
        description: "Sends a reset command to the VFD",
        units: null,
        channelType: DurapulseChannelType.DigitalOutput,
    }>,

    /// Status Channels
    fault_code: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Fault Code",
        machineName: "fault_code",
        description: "The current fault code of the VFD",
        units: null,
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    state: ChannelOverride<Durapulse_EnumInput<DurapulseStateStatus>, {
        displayName: "State",
        machineName: "state",
        description: "The current state of the VFD",
        units: null,
        channelType: DurapulseChannelType.EnumInput,
    }>,
    jog_active: ChannelOverride<Durapulse_DigitalInput, {
        displayName: "Jog Active",
        machineName: "jog_active",
        description: "Indicates whether the VFD is currently jogging",
        units: null,
        channelType: DurapulseChannelType.DigitalInput,
    }>,
    direction: ChannelOverride<Durapulse_EnumInput<DurapulseDirectionStatus>, {
        displayName: "Direction",
        machineName: "direction",
        description: "The current direction of the VFD",
        units: null,
        channelType: DurapulseChannelType.EnumInput,
    }>,
    frequency_setpoint: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Frequency Setpoint",
        machineName: "frequency_setpoint",
        description: "The current speed setpoint of the VFD in Hz",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    output_frequency: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Output Frequency",
        machineName: "output_frequency",
        description: "The speed that the VFD is currently running at.",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    output_voltage: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Output Voltage",
        machineName: "output_voltage",
        description: "The voltage that the VFD is currently outputting.",
        units: "V",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    output_current: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Output Current",
        machineName: "output_current",
        description: "The current that the VFD is currently outputting.",
        units: "A",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    // DC Bus Voltage
    dc_bus_voltage: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "DC Bus Voltage",
        machineName: "dc_bus_voltage",
        description: "The voltage of the DC bus.",
        units: "V",
    }>,
    // Warning Code
    warning_code: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Warning Code",
        machineName: "warning_code",
        description: "The current warning code of the VFD",
        units: null,
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    // Power Factor Angle   
    power_factor_angle: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Power Factor Angle",
        machineName: "power_factor_angle",
        description: "The current power factor angle of the VFD",
        units: "cos(θ)",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    // Output Torque
    output_torque: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Output Torque",
        machineName: "output_torque",
        description: "The current output torque of the VFD",
        units: "%",
        channelType: DurapulseChannelType.AnalogInput,
    }>,
    // Power Output
    output_power: ChannelOverride<Durapulse_AnalogInput, {
        displayName: "Output Power",
        machineName: "output_power",
        description: "The current power output of the VFD",
        units: "kW",
        channelType: DurapulseChannelType.AnalogInput,
    }>,

    full_load_amps_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Full Load Amps Command",
        machineName: "full_load_amps_cmd",
        description: "Sets the full load amps of the VFD",
        units: "A",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    motor_poles_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Motor Poles Command",
        machineName: "motor_poles_cmd",
        description: "Sets the number of motor poles of the VFD",
        units: null,
        channelType: DurapulseChannelType.AnalogOutput,
    }>,


    // Parameters for setting the V/Hz curve
    maximum_frequency_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Maximum Frequency Command",
        machineName: "maximum_frequency_cmd",
        description: "Sets the maximum frequency of the V/Hz curve",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    maximum_voltage_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Maximum Voltage Command",
        machineName: "maximum_voltage_cmd",
        description: "Sets the maximum voltage of the V/Hz curve",
        units: "V",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    midpoint_frequency_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Midpoint Frequency Command",
        machineName: "midpoint_frequency_cmd",
        description: "Sets the midpoint frequency of the V/Hz curve",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    midpoint_voltage_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Midpoint Voltage Command",
        machineName: "midpoint_voltage_cmd",
        description: "Sets the midpoint voltage of the V/Hz curve",
        units: "V",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    minimum_frequency_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Minimum Frequency Command",
        machineName: "minimum_frequency_cmd",
        description: "Sets the minimum frequency of the V/Hz curve",
        units: "Hz",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
    minimum_voltage_cmd: ChannelOverride<Durapulse_AnalogOutput, {
        displayName: "Minimum Voltage Command",
        machineName: "minimum_voltage_cmd",
        description: "Sets the minimum voltage of the V/Hz curve",
        units: "V",
        channelType: DurapulseChannelType.AnalogOutput,
    }>,
}

type CR011RChannels = {
    slider_pixel_width: ChannelOverride<CR011R_AnalogOutput, {
        displayName: "Slider Pixel Width",
        machineName: "slider_pixel_width",
        description: "The slider pixel width",
        units: null,
        channelType: CR011RChannelType.AnalogOutput,
        initialValue: number,
    }>,
    slider_position: ChannelOverride<CR011R_AnalogOutput, {
        displayName: "Slider Position",
        machineName: "slider_position",
        description: "The position of the slide on the light strip",
        units: null,
        channelType: CR011RChannelType.AnalogOutput,
        initialValue: number,
    }>,
    number_pixels: ChannelOverride<CR011R_AnalogOutput, {
        displayName: "Number Pixels",
        machineName: "number_pixels",
        description: "The number of pixels in the light strip",
        units: null,
        channelType: CR011RChannelType.AnalogOutput,
        initialValue: number,
    }>,

}

type TLB4ModbusTCPChannels = {
    gross_weight: ChannelOverride<TLB4ModbusTCP_AnalogInput, {
        displayName: "Gross Weight",
        machineName: "gross_weight",
        description: "Gross weight of the modbus",
        units: null,
        channelType: TLB4ModbusTCPChannelType.AnalogInput,
    }>,
    net_weight: ChannelOverride<TLB4ModbusTCP_AnalogInput, {
        displayName: "Net Weight",
        machineName: "net_weight",
        description: "Net weight of the modbus",
        units: null,
        channelType: TLB4ModbusTCPChannelType.AnalogInput,
    }>,
    peak_weight: ChannelOverride<TLB4ModbusTCP_AnalogInput, {
        displayName: "Peak Weight",
        machineName: "peak_weight",
        description: "Peak weight of the modbus",
        units: null,
        channelType: TLB4ModbusTCPChannelType.AnalogInput,
    }>,
    settings: ChannelOverride<TLB4ModbusTCP_Settings, {
        displayName: "Settings",
        machineName: "settings",
        description: "Settings config for the modbus",
        units: null,
        channelType: TLB4ModbusTCPChannelType.Settings,
        settings: {
            fullScale: 0,
            unit: TLB4ModbusTCPUnit.Pounds,
        }
    }>,
    tare_cmd: ChannelOverride<TLB4ModbusTCP_DigitalOutput, {
        displayName: "Tare Command",
        machineName: "tare_cmd",
        description: "Resets the Modbus displayed weight to zero, based one current weight"
        channelType: TLB4ModbusTCPChannelType.DigitalOutput
        units: null,
    }>,
    reset_tare_cmd: ChannelOverride<TLB4ModbusTCP_DigitalOutput, {
        displayName: "Reset Tare Command",
        machineName: "reset_tare_cmd",
        description: "Resets the Modbus displayed weight"
        channelType: TLB4ModbusTCPChannelType.DigitalOutput
        units: null,
    }>,
    exc: ChannelOverride<TLB4ModbusTCP_AnalogInput, {
        displayName: "Execution code command",
        machineName: "exc",
        description: "The code for the most recently executed command",
        channelType: TLB4ModbusTCPChannelType.AnalogInput,
        units: null,
    }>,
    aexc: ChannelOverride<TLB4ModbusTCP_AnalogInput, {
        displayName: "Auxillary execution code command",
        machineName: "aexc",
        description: "The auxillary code for the most recently executed command",
        channelType: TLB4ModbusTCPChannelType.AnalogInput,
        units: null,
    }>,
}

type SureServo2Channels = {
    /// Command Channels
    servo_on_cmd: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Servo On Command",
        machineName: "servo_on_cmd",
        description: "Command that turns the servo drive on (true) or off (false)",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    alarm_reset_cmd: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Alarm Reset Command",
        machineName: "alarm_reset_cmd",
        description: "Sends a command to reset any alarms that are active",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    jog_forward_cmd: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Jog Forward Command",
        machineName: "jog_forward_cmd",
        description: "Sends a command to jog the motor forward",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    jog_reverse_cmd: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Jog Reverse Command",
        machineName: "jog_reverse_cmd",
        description: "Sends a command to jog the motor in reverse",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    clear_pulse_count_cmd: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Clear Pulse Count Command",
        machineName: "clear_pulse_count_cmd",
        description: "Sends a command to clear the pulse count",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    homing_origin: ChannelOverride<SureServo2_DigitalOutput, {
        displayName: "Homing Origin",
        machineName: "homing_origin",
        description: "Servo drive input that indicates when the system is in it's home position. This needs to be mapped to a limit switch channel",
        units: null,
        channelType: SureServo2ChannelType.DigitalOutput,
    }>,
    homing_speed_high: ChannelOverride<SureServo2_AnalogOutput, {
        displayName: "Homing Speed High",
        machineName: "homing_speed_high",
        description: "Sets the high speed for the homing sequence",
        units: "rpm",
        channelType: SureServo2ChannelType.AnalogOutput,
        minValue: 0,
        maxValue: 3000,
        initialValue: 100
    }>,
    homing_speed_low: ChannelOverride<SureServo2_AnalogOutput, {
        displayName: "Homing Speed Low",
        machineName: "homing_speed_low",
        description: "Sets the low speed for the homing sequence",
        units: "rpm",
        channelType: SureServo2ChannelType.AnalogOutput,
        minValue: 0,
        maxValue: 3000,
        initialValue: 20
    }>,
    path_trigger_cmd: ChannelOverride<SureServo2_AnalogOutput, {
        displayName: "Path Target Command",
        machineName: "path_target_cmd",
        description: "P5.007 - Sends a command to execut the specificed path. 0 will run the homing sequence and 100 will stop path execution",
        units: null,
        channelType: SureServo2ChannelType.AnalogOutput,
        initialValue: 1000,
        minValue: 0,
        maxValue: 1000
    }>,
    jog_speed_cmd: ChannelOverride<SureServo2_AnalogOutput, {
        displayName: "Jog Speed Command",
        machineName: "jog_speed_cmd",
        description: "Sets thespeed of the jog command",
        units: "rpm",
        channelType: SureServo2ChannelType.AnalogOutput,
        minValue: 0,
        maxValue: 3000
        initialValue: 100
    }>,
    path_config: ChannelOverride<SureServo2PathConfig_Position, {
        displayName: "Path Config",
        machineName: "path_config",
        description: "The configuration of the path that the servo will follow",
        channelType: SureServo2ChannelType.PathConfig_Position
    }> | ChannelOverride<SureServo2PathConfig_Speed, {
        displayName: "Path Config",
        machineName: "path_config",
        description: "The configuration of the path that the servo will follow",
        channelType: SureServo2ChannelType.PathConfig_Speed
    }>,

    /// Input Channels
    alarm_code: ChannelOverride<SureServo2_AnalogInput, {
        displayName: "Alarm Code",
        machineName: "alarm_code",
        description: "The current alarm code of the servo drive",
        units: null,
        channelType: SureServo2ChannelType.AnalogInput,
    }>,
    servo_on_status: ChannelOverride<SureServo2_DigitalInput, {
        displayName: "Servo On Status",
        machineName: "servo_on_status",
        description: "Indicates whether the servo drive is currently on",
        units: null,
        channelType: SureServo2ChannelType.DigitalInput,
    }>,
    servo_ready_status: ChannelOverride<SureServo2_DigitalInput, {
        displayName: "Servo Ready Status",
        machineName: "servo_ready_status",
        description: "Indicates whether the servo drive is ready to run",
        units: null,
        channelType: SureServo2ChannelType.DigitalInput,
    }>,
    motor_speed_zero: ChannelOverride<SureServo2_DigitalInput, {
        displayName: "Motor Speed Zero",
        machineName: "motor_speed_zero",
        description: "Indicates whether the motor speed is zero",
        units: null,
        channelType: SureServo2ChannelType.DigitalInput,
    }>,
    homing_complete: ChannelOverride<SureServo2_DigitalInput, {
        displayName: "Homing Complete",
        machineName: "homing_complete",
        description: "Indicates whether the motor has completed homing",
        units: null,
        channelType: SureServo2ChannelType.DigitalInput,
    }>,
    at_target_position: ChannelOverride<SureServo2_DigitalInput, {
        displayName: "At Target Position",
        machineName: "at_target_position",
        description: "Indicates whether the motor is at the target position",
        units: null,
        channelType: SureServo2ChannelType.DigitalInput,
    }>,
    position: ChannelOverride<SureServo2_AnalogInput, {
        displayName: "Position",
        machineName: "position",
        description: "The current position of the servo motor",
        units: "PPU",
        channelType: SureServo2ChannelType.AnalogInput,
    }>,
    position_cmd_feedback: ChannelOverride<SureServo2_AnalogInput, {
        displayName: "Position Command Feedback",
        machineName: "position_cmd_feedback",
        description: "The commanded position of the servo motor",
        units: "PPU",
        channelType: SureServo2ChannelType.AnalogInput,
    }>,
    average_load: ChannelOverride<SureServo2_AnalogInput, {
        displayName: "Average Load",
        machineName: "average_load",
        description: "The average load on the motor",
        units: "%",
        channelType: SureServo2ChannelType.AnalogInput,
    }>,



}


type ContrixRFIDChannels = {
    rssi: ChannelOverride<IoLink_AnalogInput, {
        displayName: "RSSI",
        machineName: "rssi",
        description: "The signal strength of the RFID tag",
        units: "dBm",
        channelType: IoLinkChannelType.AnalogInput,
    }>,
    tagId: ChannelOverride<IoLink_AnalogInput, {
        displayName: "Tag ID",
        machineName: "tagId",
        description: "The ID of the RFID tag",
        units: null,
        channelType: IoLinkChannelType.AnalogInput,
    }>,
    antenna_on: ChannelOverride<IoLink_DigitalInput, {
        displayName: "Antenna On",
        machineName: "antenna_on",
        description: "Indicates whether the RFID field is on",
        units: null,
        channelType: IoLinkChannelType.DigitalInput,
    }>,
    tag_present: ChannelOverride<IoLink_DigitalInput, {
        displayName: "Tag Present",
        machineName: "tag_present",
        description: "Indicates whether an RFID tag is present",
        units: null,
        channelType: IoLinkChannelType.DigitalInput,
    }>,
    error_code: ChannelOverride<IoLink_AnalogInput, {
        displayName: "Error Code",
        machineName: "error_code",
        description: "The current error code of the RFID tag",
        units: null,
        channelType: IoLinkChannelType.AnalogInput,
    }>,
}






export enum SureServo2PathType {
    SPEED = "SPEED", // path that runs the servo at a constant speed
    POSITION = "POSITION", // path that moves the servo to a specific position
}





export interface DuraPulse extends BaseDevice {
    deviceType: DeviceType.DuraPulse;
    channels: DuraPulseChannels;
    manualConfig: boolean;
}

export interface SureServo2 extends BaseDevice {
    deviceType: DeviceType.SureServo2;
    channels: SureServo2Channels;
}

export interface TLB4ModbusTCP extends BaseDevice {
    deviceType: DeviceType.TLB4ModbusTCP;
    channels: TLB4ModbusTCPChannels;
}

export interface IoLinkMaster_54631 extends BaseDevice {
    deviceType: DeviceType.IoLinkMaster_54631;
    subdevices: FixedLengthArray<IoLinkDevice | null, 8>;

}

export interface FerryRotoMachine extends BaseDevice {
    deviceType: DeviceType.FerryRotoMachine;
    channels: FerryRotoMachineChannels;
}

export interface ReolinkCamera extends BaseDevice {
    deviceType: DeviceType.ReolinkCamera;
    channels: ReolinkCameraMachineChannels;
}

export interface CR011R extends BaseDevice {
    deviceType: DeviceType.CR011R;
    channels: CR011RChannels;
}

export type IoLinkDevice = IoLinkDevice_ContrinexRFID;

export enum IoLinkDeviceType {
    CONTRINEX_RFID = "CONTRINEX_RFID",
}

export interface IoLinkDevice_Base extends CoreConfigData {
    type: IoLinkDeviceType;
}


export interface IoLinkDevice_ContrinexRFID extends IoLinkDevice_Base {
    channels: ContrixRFIDChannels;
    type: IoLinkDeviceType.CONTRINEX_RFID;
}

export function getDX200Channels(): DX200Channels {
    return {
        alarm_reset_cmd: {
            id: generateId(),
            displayName: "Alarm Reset Command",
            machineName: "alarm_reset_cmd",
            description: "Sends a command to reset any alarms that are active",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        start_cmd: {
            id: generateId(),
            displayName: "Start Command",
            machineName: "start_cmd",
            description: "Sends a command to start the robot",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        servo_on_cmd: {
            id: generateId(),
            displayName: "Servo On Command",
            machineName: "servo_on_cmd",
            description: "Sends a command to turn the servo on",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        servo_off_cmd: {
            id: generateId(),
            displayName: "Servo Off Command",
            machineName: "servo_off_cmd",
            description: "Sends a command to turn the servo off",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        hold_cmd: {
            id: generateId(),
            displayName: "Hold Command",
            machineName: "hold_cmd",
            description: "Sends a command to hold the robot",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        continue_cycle_cmd: {
            id: generateId(),
            displayName: "Continue Cycle Command",
            machineName: "continue_cycle_cmd",
            description: "Sends a command to continue the robot's cycle",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        call_master_job_cmd: {
            id: generateId(),
            displayName: "Call Master Job Command",
            machineName: "call_master_job_cmd",
            description: "Sends a command to call the master job",
            units: null,
            channelType: DX200ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        program_number: {
            id: generateId(),
            displayName: "Program Number",
            machineName: "program_number",
            description: "Sets the program number to run",
            units: null,
            channelType: DX200ChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 0,
            maxValue: 9999,
            minValue: 0
        },
        error: {
            id: generateId(),
            displayName: "Error",
            machineName: "error",
            description: "Indicates whether an error has occurred",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        pendant_estop_ok: {
            id: generateId(),
            displayName: "Pendant E-Stop OK",
            machineName: "pendant_estop_ok",
            description: "Indicates whether the pendant E-stop is OK",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        major_alarm: {
            id: generateId(),
            displayName: "Major Alarm",
            machineName: "major_alarm",
            description: "Indicates whether a major alarm has occurred",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        top_master_job: {
            id: generateId(),
            displayName: "Top Master Job",
            machineName: "top_master_job",
            description: "Indicates that the program execution is at the top of the master job",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        remote_mode: {
            id: generateId(),
            displayName: "Remote Mode",
            machineName: "remote_mode",
            description: "Indicates whether the robot is in remote mode",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        teach_mode: {
            id: generateId(),
            displayName: "Teach Mode",
            machineName: "teach_mode",
            description: "Indicates whether the robot is in teach mode",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        play_mode: {
            id: generateId(),
            displayName: "Play Mode",
            machineName: "play_mode",
            description: "Indicates whether the robot is in play mode",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        servo_on: {
            id: generateId(),
            displayName: "Servo On",
            machineName: "servo_on",
            description: "Indicates whether the servo is on",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        holding: {
            id: generateId(),
            displayName: "Holding",
            machineName: "holding",
            description: "Indicates whether the robot is holding",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        running: {
            id: generateId(),
            displayName: "Running",
            machineName: "running",
            description: "Indicates whether the robot is running",
            units: null,
            channelType: DX200ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
    }
}

export function getYRC1000Channels(): YRC1000Channels {
    return {
        alarm_reset_cmd: {
            id: generateId(),
            displayName: "Alarm Reset Command",
            machineName: "alarm_reset_cmd",
            description: "Sends a command to reset any alarms that are active",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        start_cmd: {
            id: generateId(),
            displayName: "Start Command",
            machineName: "start_cmd",
            description: "Sends a command to start the robot",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        servo_on_cmd: {
            id: generateId(),
            displayName: "Servo On Command",
            machineName: "servo_on_cmd",
            description: "Sends a command to turn the servo on",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        servo_off_cmd: {
            id: generateId(),
            displayName: "Servo Off Command",
            machineName: "servo_off_cmd",
            description: "Sends a command to turn the servo off",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        hold_cmd: {
            id: generateId(),
            displayName: "Hold Command",
            machineName: "hold_cmd",
            description: "Sends a command to hold the robot",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        continue_cycle_cmd: {
            id: generateId(),
            displayName: "Continue Cycle Command",
            machineName: "continue_cycle_cmd",
            description: "Sends a command to continue the robot's cycle",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        call_master_job_cmd: {
            id: generateId(),
            displayName: "Call Master Job Command",
            machineName: "call_master_job_cmd",
            description: "Sends a command to call the master job",
            units: null,
            channelType: YRC1000ChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        program_number: {
            id: generateId(),
            displayName: "Program Number",
            machineName: "program_number",
            description: "Sets the program number to run",
            units: null,
            channelType: YRC1000ChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 0,
            maxValue: 9999,
            minValue: 0
        },
        error: {
            id: generateId(),
            displayName: "Error",
            machineName: "error",
            description: "Indicates whether an error has occurred",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        pendant_estop_ok: {
            id: generateId(),
            displayName: "Pendant E-Stop OK",
            machineName: "pendant_estop_ok",
            description: "Indicates whether the pendant E-stop is OK",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        major_alarm: {
            id: generateId(),
            displayName: "Major Alarm",
            machineName: "major_alarm",
            description: "Indicates whether a major alarm has occurred",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        top_master_job: {
            id: generateId(),
            displayName: "Top Master Job",
            machineName: "top_master_job",
            description: "Indicates that the program execution is at the top of the master job",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        remote_mode: {
            id: generateId(),
            displayName: "Remote Mode",
            machineName: "remote_mode",
            description: "Indicates whether the robot is in remote mode",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        teach_mode: {
            id: generateId(),
            displayName: "Teach Mode",
            machineName: "teach_mode",
            description: "Indicates whether the robot is in teach mode",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        play_mode: {
            id: generateId(),
            displayName: "Play Mode",
            machineName: "play_mode",
            description: "Indicates whether the robot is in play mode",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        servo_on: {
            id: generateId(),
            displayName: "Servo On",
            machineName: "servo_on",
            description: "Indicates whether the servo is on",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        holding: {
            id: generateId(),
            displayName: "Holding",
            machineName: "holding",
            description: "Indicates whether the robot is holding",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        running: {
            id: generateId(),
            displayName: "Running",
            machineName: "running",
            description: "Indicates whether the robot is running",
            units: null,
            channelType: YRC1000ChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        xPos: {
            id: generateId(),
            displayName: "xPos",
            machineName: "xPos",
            description: "The current x position of the robot",
            units: "mm",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        yPos: {
            id: generateId(),
            displayName: "yPos",
            machineName: "yPos",
            description: "The current y position of the robot",
            units: "mm",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        zPos: {
            id: generateId(),
            displayName: "zPos",
            machineName: "zPos",
            description: "The current z position of the robot",
            units: "mm",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        aPos: {
            id: generateId(),
            displayName: "aPos",
            machineName: "aPos",
            description: "The current a rotation of the robot",
            units: "deg",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        bPos: {
            id: generateId(),
            displayName: "bPos",
            machineName: "bPos",
            description: "The current b rotation of the robot",
            units: "deg",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        cPos: {
            id: generateId(),
            displayName: "cPos",
            machineName: "cPos",
            description: "The current c rotation of the robot",
            units: "deg",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        speed: {
            id: generateId(),
            displayName: "Speed",
            machineName: "speed",
            description: "The current speed of the robot",
            units: "mm/s",
            channelType: YRC1000ChannelType.AnalogInput,
            updatedOn: new Date(),
        },
    }
}

export function getEmptyYRC1000UserChannels(): YRC1000UserChannels {
    return {
        analogInputs: [],
        analogOutputs: [],
        digitalInputs: [],
        digitalOutputs: [],
    }
}



export function getDuraPulseChannels(): DuraPulseChannels {
    return {
        motor_poles_cmd: {
            id: generateId(),
            displayName: "Motor Poles Command",
            machineName: "motor_poles_cmd",
            description: "Sets the number of motor poles of the VFD",
            units: null,
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 4,
            maxValue: 20,
            minValue: 0
        },
        full_load_amps_cmd: {
            id: generateId(),
            displayName: "Full Load Amps Command",
            machineName: "full_load_amps_cmd",
            description: "Sets the full load amps of the VFD",
            units: "A",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 10,
            maxValue: 100,
            minValue: 0
        },
        state_cmd: {
            id: generateId(),
            displayName: "State Command",
            machineName: "state_cmd",
            description: "Sets the VFD state",
            units: null,
            channelType: DurapulseChannelType.EnumOutput,
            initialValue: DurapulseStateCommand.Stop,
            updatedOn: new Date(),
            options: [DurapulseStateCommand.Run, DurapulseStateCommand.Stop, DurapulseStateCommand.EnableJog]
        },
        direction_cmd: {
            id: generateId(),
            displayName: "Direction Command",
            machineName: "direction_cmd",
            description: "Sets the VFD direction",
            units: null,
            channelType: DurapulseChannelType.EnumOutput,
            initialValue: DurapulseDirectionCommand.Forward,
            updatedOn: new Date(),
            options: [DurapulseDirectionCommand.Forward, DurapulseDirectionCommand.Reverse]
        },
        frequency_cmd: {
            id: generateId(),
            displayName: "Frequency Command",
            machineName: "frequency_cmd",
            description: "Sets the VFD speed in Hz",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 0,
            maxValue: 10000,
            minValue: 0
        },
        acceleration_time_cmd: {
            id: generateId(),
            displayName: "Acceleration Time Command",
            machineName: "acceleration_time_cmd",
            description: "Sets the acceleration time of the VFD as defined by the time it takes to go from 0 to max speed.",
            units: "ms",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 5000,
            maxValue: 10000,
            minValue: 0
        },
        stop_method_cmd: {
            id: generateId(),
            displayName: "Stop Method Command",
            description: "Sets the method that the VFD will use to stop",
            machineName: "stop_method_cmd",
            initialValue: DurapulseStopMethod.RAMP_TO_STOP,
            channelType: DurapulseChannelType.EnumOutput,
            units: null,
            updatedOn: new Date(),
            options: [DurapulseStopMethod.RAMP_TO_STOP, DurapulseStopMethod.COAST_TO_STOP],
        },
        reset_cmd: {
            id: generateId(),
            displayName: "Reset Command",
            machineName: "reset_cmd",
            description: "Sends a reset command to the VFD",
            units: null,
            channelType: DurapulseChannelType.DigitalOutput,
            updatedOn: new Date(),
            initialValue: false
        },
        fault_code: {
            id: generateId(),
            displayName: "Fault Code",
            machineName: "fault_code",
            description: "The current fault code of the VFD",
            units: null,
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        state: {
            id: generateId(),
            displayName: "State",
            machineName: "state",
            description: "The current state of the VFD",
            units: null,
            channelType: DurapulseChannelType.EnumInput,
            updatedOn: new Date(),
            options: [DurapulseStateStatus.Run, DurapulseStateStatus.Standby, DurapulseStateStatus.DecelDuringStop, DurapulseStateStatus.Stop]
        },
        jog_active: {
            id: generateId(),
            displayName: "Jog Active",
            machineName: "jog_active",
            description: "Indicates whether the VFD is currently jogging",
            units: null,
            channelType: DurapulseChannelType.DigitalInput,
            updatedOn: new Date(),
        },
        direction: {
            id: generateId(),
            displayName: "Direction",
            machineName: "direction",
            description: "The current direction of the VFD",
            units: null,
            channelType: DurapulseChannelType.EnumInput,
            updatedOn: new Date(),
            options: [DurapulseDirectionStatus.Forward, DurapulseDirectionStatus.Reverse, DurapulseDirectionStatus.TransitionFromReverseToForward, DurapulseDirectionStatus.TransitionFromForwardToReverse]
        },
        frequency_setpoint: {
            id: generateId(),
            displayName: "Frequency Setpoint",
            machineName: "frequency_setpoint",
            description: "The current speed setpoint of the VFD in Hz",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        output_frequency: {
            id: generateId(),
            displayName: "Output Frequency",
            machineName: "output_frequency",
            description: "The speed that the VFD is currently running at.",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        output_voltage: {
            id: generateId(),
            displayName: "Output Voltage",
            machineName: "output_voltage",
            description: "The voltage that the VFD is currently outputting.",
            units: "V",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        output_current: {
            id: generateId(),
            displayName: "Output Current",
            machineName: "output_current",
            description: "The current that the VFD is currently outputting.",
            units: "A",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        dc_bus_voltage: {
            id: generateId(),
            displayName: "DC Bus Voltage",
            machineName: "dc_bus_voltage",
            description: "The voltage of the DC bus.",
            units: "V",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        warning_code: {
            id: generateId(),
            displayName: "Warning Code",
            machineName: "warning_code",
            description: "The current warning code of the VFD",
            units: null,
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        power_factor_angle: {
            id: generateId(),
            displayName: "Power Factor Angle",
            machineName: "power_factor_angle",
            description: "The current power factor angle of the VFD",
            units: "cos(θ)",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        output_torque: {
            id: generateId(),
            displayName: "Output Torque",
            machineName: "output_torque",
            description: "The current output torque of the VFD",
            units: "%",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        output_power: {
            id: generateId(),
            displayName: "Output Power",
            machineName: "output_power",
            description: "The current power output of the VFD",
            units: "kW",
            channelType: DurapulseChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        maximum_frequency_cmd: {
            id: generateId(),
            displayName: "Maximum Frequency Command",
            machineName: "maximum_frequency_cmd",
            description: "Sets the maximum frequency of the V/Hz curve",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 60,
            maxValue: 600,
            minValue: 0
        },
        maximum_voltage_cmd: {
            id: generateId(),
            displayName: "Maximum Voltage Command",
            machineName: "maximum_voltage_cmd",
            description: "Sets the maximum voltage of the V/Hz curve",
            units: "V",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 480,
            maxValue: 480,
            minValue: 0
        },
        midpoint_frequency_cmd: {
            id: generateId(),
            displayName: "Midpoint Frequency Command",
            machineName: "midpoint_frequency_cmd",
            description: "Sets the midpoint frequency of the V/Hz curve",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 3,
            maxValue: 600,
            minValue: 0
        },
        midpoint_voltage_cmd: {
            id: generateId(),
            displayName: "Midpoint Voltage Command",
            machineName: "midpoint_voltage_cmd",
            description: "Sets the midpoint voltage of the V/Hz curve",
            units: "V",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 22,
            maxValue: 480,
            minValue: 0
        },
        minimum_frequency_cmd: {
            id: generateId(),
            displayName: "Minimum Frequency Command",
            machineName: "minimum_frequency_cmd",
            description: "Sets the minimum frequency of the V/Hz curve",
            units: "Hz",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 0.5,
            maxValue: 600,
            minValue: 0
        },
        minimum_voltage_cmd: {
            id: generateId(),
            displayName: "Minimum Voltage Command",
            machineName: "minimum_voltage_cmd",
            description: "Sets the minimum voltage of the V/Hz curve",
            units: "V",
            channelType: DurapulseChannelType.AnalogOutput,
            updatedOn: new Date(),
            initialValue: 2.0,
            maxValue: 480,
            minValue: 0
        },

    }
}

export function getCR011RChannels(): CR011RChannels {
    return {
        slider_pixel_width: {
            channelType: CR011RChannelType.AnalogOutput,
            displayName: "Slider Pixel Width",
            machineName: "slider_pixel_width",
            description: "The slider pixel width",
            units: null,
            id: generateId(),
            updatedOn: new Date(),
            initialValue: 1,
        },
        slider_position: {
            channelType: CR011RChannelType.AnalogOutput,
            displayName: "Slider Position",
            machineName: "slider_position",
            description: "The position of the slide on the light strip",
            units: null,
            id: generateId(),
            updatedOn: new Date(),
            initialValue: 0,
        },
        number_pixels: {
            channelType: CR011RChannelType.AnalogOutput,
            displayName: "Number Pixels",
            machineName: "number_pixels",
            description: "The number of pixels in the light strip",
            units: null,
            id: generateId(),
            updatedOn: new Date(),
            initialValue: 100,
        },
    }
}

export function getTLB4ModbusTCPChannels(): TLB4ModbusTCPChannels {
    return {
        gross_weight: {
            id: generateId(),
            displayName: "Gross Weight",
            machineName: "gross_weight",
            description: "Gross weight of the modbus",
            units: null,
            channelType: TLB4ModbusTCPChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        net_weight: {
            id: generateId(),
            displayName: "Net Weight",
            machineName: "net_weight",
            description: "Net weight of the modbus",
            units: null,
            channelType: TLB4ModbusTCPChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        peak_weight: {
            id: generateId(),
            displayName: "Peak Weight",
            machineName: "peak_weight",
            description: "Peak weight of the modbus",
            units: null,
            channelType: TLB4ModbusTCPChannelType.AnalogInput,
            updatedOn: new Date(),
        },
        settings: {
            id: generateId(),
            displayName: "Settings",
            machineName: "settings",
            description: "Settings config for the modbus",
            channelType: TLB4ModbusTCPChannelType.Settings,
            units: null,
            updatedOn: new Date(),
            settings: {
                fullScale: 0,
                unit: TLB4ModbusTCPUnit.Pounds,
            }
        },
        tare_cmd: {
            id: generateId(),
            displayName: "Tare Command",
            machineName: "tare_cmd",
            description: "Resets the Modbus displayed weight to zero, based one current weight",
            channelType: TLB4ModbusTCPChannelType.DigitalOutput,
            updatedOn: new Date(),
            units: null,
            initialValue: false,
        },
        reset_tare_cmd: {
            id: generateId(),
            displayName: "Reset Tare Command",
            machineName: "reset_tare_cmd",
            description: "Resets the Modbus displayed weight",
            channelType: TLB4ModbusTCPChannelType.DigitalOutput,
            updatedOn: new Date(),
            units: null,
            initialValue: false,
        },
        exc: {
            id: generateId(),
            displayName: "Execution code command",
            machineName: "exc",
            description: "The code for the most recently executed command",
            channelType: TLB4ModbusTCPChannelType.AnalogInput,
            updatedOn: new Date(),
            units: null,
        },
        aexc: {
            id: generateId(),
            displayName: "Auxillary execution code command",
            machineName: "aexc",
            description: "The auxillary code for the most recently executed command",
            channelType: TLB4ModbusTCPChannelType.AnalogInput,
            updatedOn: new Date(),
            units: null,
        }
    }
}

export function getSureservo2Channels(): SureServo2Channels {
    return {
        servo_on_cmd: {
            id: generateId(),
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Command that turns the servo drive on (true) or off (false)",
            displayName: "Servo On Command",
            initialValue: false,
            machineName: "servo_on_cmd",
            units: null,
            updatedOn: new Date(),
        },
        alarm_reset_cmd: {
            id: generateId(),
            displayName: "Alarm Reset Command",
            machineName: "alarm_reset_cmd",
            initialValue: false,
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Sends a command to reset any alarms that are active",
            updatedOn: new Date(),
            units: null
        },
        jog_forward_cmd: {
            id: generateId(),
            displayName: "Jog Forward Command",
            machineName: "jog_forward_cmd",
            initialValue: false,
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Sends a command to jog the motor forward",
            updatedOn: new Date(),
            units: null
        },
        jog_reverse_cmd: {
            id: generateId(),
            displayName: "Jog Reverse Command",
            machineName: "jog_reverse_cmd",
            initialValue: false,
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Sends a command to jog the motor in reverse",
            updatedOn: new Date(),
            units: null
        },
        clear_pulse_count_cmd: {
            id: generateId(),
            displayName: "Clear Pulse Count Command",
            machineName: "clear_pulse_count_cmd",
            initialValue: false,
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Sends a command to clear the pulse count",
            updatedOn: new Date(),
            units: null
        },
        homing_origin: {
            id: generateId(),
            displayName: "Homing Origin",
            machineName: "homing_origin",
            initialValue: false,
            channelType: SureServo2ChannelType.DigitalOutput,
            description: "Servo drive input that indicates when the system is in it's home position. This needs to be mapped to a limit switch channel",
            updatedOn: new Date(),
            units: null
        },
        homing_speed_high: {
            id: generateId(),
            displayName: "Homing Speed High",
            machineName: "homing_speed_high",
            channelType: SureServo2ChannelType.AnalogOutput,
            description: "Sets the high speed for the homing sequence",
            initialValue: 100,
            maxValue: 3000,
            minValue: 0,
            units: "rpm",
            updatedOn: new Date(),
        },
        homing_speed_low: {
            id: generateId(),
            displayName: "Homing Speed Low",
            machineName: "homing_speed_low",
            channelType: SureServo2ChannelType.AnalogOutput,
            description: "Sets the low speed for the homing sequence",
            initialValue: 20,
            maxValue: 3000,
            minValue: 0,
            units: "rpm",
            updatedOn: new Date(),
        },

        path_trigger_cmd: {
            id: generateId(),
            displayName: "Path Target Command",
            machineName: "path_target_cmd",
            initialValue: 1000,
            minValue: 0,
            maxValue: 1000,
            channelType: SureServo2ChannelType.AnalogOutput,
            description: "P5.007 - Sends a command to execut the specificed path. 0 will run the homing sequence and 100 will stop path execution",
            updatedOn: new Date(),
            units: null
        },
        path_config: {
            id: generateId(),
            type: SureServo2PathType.POSITION,
            accelerationTime_ms: 10000,
            channelType: SureServo2ChannelType.PathConfig_Position,
            decelerationTime_ms: 10000,
            description: "The configuration of the path that the servo will follow",
            displayName: "Path Config",
            machineName: "path_config",
            positionCmdType: SureServo2PathPositionCmdType.ABSOLUTE,
            targetSpeed: 10,
            targetPosition: 0,
            units: null,
            updatedOn: new Date(),
        },
        jog_speed_cmd: {
            id: generateId(),
            displayName: "Jog Speed Command",
            machineName: "jog_speed_cmd",
            initialValue: 100,
            minValue: 0,
            maxValue: 3000,
            channelType: SureServo2ChannelType.AnalogOutput,
            description: "Sets thespeed of the jog command",
            updatedOn: new Date(),
            units: "rpm"
        },

        alarm_code: {
            id: generateId(),
            displayName: "Alarm Code",
            machineName: "alarm_code",
            channelType: SureServo2ChannelType.AnalogInput,
            description: "The current alarm code of the servo drive",
            updatedOn: new Date(),
            units: null
        },
        servo_on_status: {
            id: generateId(),
            displayName: "Servo On Status",
            machineName: "servo_on_status",
            channelType: SureServo2ChannelType.DigitalInput,
            description: "Indicates whether the servo drive is currently on",
            updatedOn: new Date(),
            units: null
        },
        servo_ready_status: {
            id: generateId(),
            displayName: "Servo Ready Status",
            machineName: "servo_ready_status",
            channelType: SureServo2ChannelType.DigitalInput,
            description: "Indicates whether the servo drive is ready to run",
            updatedOn: new Date(),
            units: null
        },
        homing_complete: {
            id: generateId(),
            displayName: "Homing Complete",
            machineName: "homing_complete",
            channelType: SureServo2ChannelType.DigitalInput,
            description: "Indicates whether the motor has completed homing",
            updatedOn: new Date(),
            units: null
        },
        motor_speed_zero: {
            id: generateId(),
            displayName: "Motor Speed Zero",
            machineName: "motor_speed_zero",
            channelType: SureServo2ChannelType.DigitalInput,
            description: "Indicates whether the motor speed is zero",
            updatedOn: new Date(),
            units: null
        },
        at_target_position: {
            id: generateId(),
            displayName: "At Target Position",
            machineName: "at_target_position",
            channelType: SureServo2ChannelType.DigitalInput,
            description: "Indicates whether the motor is at the target position",
            updatedOn: new Date(),
            units: null
        },
        position: {
            id: generateId(),
            displayName: "Position",
            machineName: "position",
            channelType: SureServo2ChannelType.AnalogInput,
            description: "The current position of the servo motor",
            updatedOn: new Date(),
            units: "PPU"
        },
        position_cmd_feedback: {
            id: generateId(),
            displayName: "Position Command Feedback",
            machineName: "position_cmd_feedback",
            channelType: SureServo2ChannelType.AnalogInput,
            description: "The commanded position of the servo motor",
            updatedOn: new Date(),
            units: "PPU"
        },
        average_load: {
            id: generateId(),
            displayName: "Average Load",
            machineName: "average_load",
            channelType: SureServo2ChannelType.AnalogInput,
            description: "The average load on the motor",
            updatedOn: new Date(),
            units: "%"
        },
    }
}


export function getContrixRFIDChannels(): ContrixRFIDChannels {
    return {
        rssi: {
            id: generateId(),
            displayName: "RSSI",
            machineName: "rssi",
            channelType: IoLinkChannelType.AnalogInput,
            description: "The signal strength of the RFID tag",
            updatedOn: new Date(),
            units: "dBm"
        },
        tagId: {
            id: generateId(),
            displayName: "Tag ID",
            machineName: "tagId",
            channelType: IoLinkChannelType.AnalogInput,
            description: "The ID of the RFID tag",
            updatedOn: new Date(),
            units: null
        },
        antenna_on: {
            id: generateId(),
            displayName: "Antenna On",
            machineName: "antenna_on",
            channelType: IoLinkChannelType.DigitalInput,
            description: "Indicates whether the RFID field is on",
            updatedOn: new Date(),
            units: null
        },
        tag_present: {
            id: generateId(),
            displayName: "Tag Present",
            machineName: "tag_present",
            channelType: IoLinkChannelType.DigitalInput,
            description: "Indicates whether an RFID tag is present",
            updatedOn: new Date(),
            units: null
        },
        error_code: {
            id: generateId(),
            displayName: "Error Code",
            machineName: "error_code",
            channelType: IoLinkChannelType.AnalogInput,
            description: "The current error code of the RFID tag",
            updatedOn: new Date(),
            units: null
        },
    }
}



interface BaseDeviceData {
    name: string;
    description: string;
    productImageUrl: string;
    datasheetUrl: string;
    startingIPAddressRangeNumber: number | null;
}

export const DeviceData: Record<DeviceType, BaseDeviceData> = {
    [DeviceType.Productivity1000]: {
        name: "Productivity 1000",
        description: "Automation Direct PLC",
        productImageUrl: "/assets/images/productivity-1000/p1-540.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-540.pdf",
        startingIPAddressRangeNumber: 10
    },
    [DeviceType.DX200]: {
        name: "DX200",
        description: "Yaskawa Robot Controller",
        productImageUrl: "/assets/images/dx200/dx200.jpg",
        datasheetUrl: "",
        startingIPAddressRangeNumber: 20
    },
    [DeviceType.DuraPulse]: {
        name: "DuraPulse",
        description: "Automation Direct VFD (Modbus)",
        productImageUrl: "/assets/images/durapulse/durapulse.jpg",
        datasheetUrl: "",
        startingIPAddressRangeNumber: 30,
    },
    [DeviceType.SureServo2]: {
        name: "SureServo2",
        description: "Automation Direct Servo Drive (Modbus)",
        productImageUrl: "/assets/images/sure-servo-2/sure-servo-2.jpg",
        datasheetUrl: "",
        startingIPAddressRangeNumber: 50,
    },
    [DeviceType.IoLinkMaster_54631]: {
        name: "IoLink Master 54631",
        description: "Murrelektronik IO-Link Master",
        productImageUrl: "/assets/images/murrelektronik/murrelektronik_54631.jpg",
        datasheetUrl: "/assets/datasheets/murrelektronik_54631.pdf",
        startingIPAddressRangeNumber: 110,
    },
    [DeviceType.YRC1000]: {
        name: "YRC1000",
        description: "Yaskawa Robot Controller",
        productImageUrl: "/assets/images/yrc1000/yrc1000.png",
        datasheetUrl: "",
        startingIPAddressRangeNumber: 20
    },
    [DeviceType.FerryRotoMachine]: {
        name: "Ferry Roto Machine",
        description: "Ferry rotational molding machine",
        productImageUrl: "/assets/images/ferry-industries/ferry-logo.jpg",
        datasheetUrl: "",
        startingIPAddressRangeNumber: null
    },
    [DeviceType.ReolinkCamera]: {
        name: "Camera",
        description: "Security camera",
        productImageUrl: "/assets/images/reolink-camera/reolink-camera.jpg",
        datasheetUrl: "",
        startingIPAddressRangeNumber: null
    },
    [DeviceType.TLB4ModbusTCP]: {
        name: "TLB4 Modbus/TCP",
        description: "Digital Weight Transmitter",
        productImageUrl: "/assets/images/tlb4-modbus-tcp/tlb4-modbus-tcp.jpg",
        datasheetUrl: "/assets/datasheets/tlb4-modbus-tcp-manual.pdf",
        startingIPAddressRangeNumber: null
    },
    [DeviceType.CR011R]: {
        name: "CR011R",
        description: "Bi-directional ArtNet Controller",
        productImageUrl: "/assets/images/cr011r/cr011r.jpg",
        datasheetUrl: "/assets/datasheets/cr011r/art-net.pdf",
        startingIPAddressRangeNumber: null
    },
}


export const IoLinkDeviceData: Record<IoLinkDeviceType, BaseSubcomponentData> = {
    [IoLinkDeviceType.CONTRINEX_RFID]: {
        name: "Contrinex RLH-M18PA-NIS",
        description: "Contrinex RFID read-write module",
        datasheetUrl: "/assets/datasheets/contrinex_rfid_RLH-M18PA.pdf",
        productImageUrl: "/assets/images/contrinex/Contrinex-RLH-M18PA-NIS-pic.png",
    }
}


///////////////////////////////////////////////////
///////////////// Subcomponents////////////////////
///////////////////////////////////////////////////

// don't export since users shouldn't be able to directly create a subcomponent
interface BaseSubcomponent extends CoreConfigData {
    subcomponentType: SubcomponentType;
}

export enum Productivity1000CardType {

    P1_15TD2 = "P1_15TD2", // 15-point digital output

    P1_16ND3 = "P1_16ND3", // 16-point digital input - 24VDC
    P1_08NA = "P1_08NA", // 8-point digital input - 120VAC

    P1_15CDD2 = "P1_15CDD2", // 15-point digital combo module

    P1_04DAL_1 = "P1_04DAL_1", // 4-channel analog output
    P1_08DAL_1 = "P1_08DAL_1", // 8-channel analog output

    P1_04AD_1 = "P1_04AD_1", // 4-channel analog input, 16-bit
    P1_04ADL_1 = "P1_04ADL_1", // 4-channel analog input, 13-bit
    P1_08ADL_1 = "P1_08ADL_1", // 8-channel analog input, 13-bit
    P1_04RTD = "P1_04RTD", // 4-channel RTD input
    P1_04THM = "P1_04THM", // 4-channel thermocouple input

}


export type SubcomponentType = Productivity1000CardType;
export type Productivity1000Card =
    Productivity1000_P1_15TD2 |
    Productivity1000_P1_16ND3 |
    Productivity1000_P1_15CDD2 |
    Productivity1000_P1_04DAL_1 |
    Productivity1000_P1_08DAL_1 |
    Productivity1000_P1_04AD_1 |
    Productivity1000_P1_04ADL_1 |
    Productivity1000_P1_08ADL_1 |
    Productivity1000_P1_04RTD |
    Productivity1000_P1_08NA |
    Productivity1000_P1_04THM;



export interface Productivity1000_P1_15TD2 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_15TD2
    channels: FixedLengthArray<(Productivity1000_DigitalOutput | null), 15>

}

export interface Productivity1000_P1_16ND3 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_16ND3
    channels: FixedLengthArray<(Productivity1000_DigitalInput | null), 16>
}

export interface Productivity1000_P1_08NA extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_08NA
    channels: FixedLengthArray<(Productivity1000_DigitalInput | null), 8>
}

export interface Productivity1000_P1_15CDD2 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_15CDD2
    channels: FixedLengthArray<(Productivity1000_DigitalInput | Productivity1000_DigitalOutput | null), 15>
}

export interface Productivity1000_P1_04DAL_1 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_04DAL_1
    channels: FixedLengthArray<(Productivity1000_AnalogOutput | null), 4>
}

export interface Productivity1000_P1_08DAL_1 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_08DAL_1
    channels: FixedLengthArray<(Productivity1000_AnalogOutput | null), 8>
}

export interface Productivity1000_P1_04AD_1 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_04AD_1
    channels: FixedLengthArray<(Productivity1000_AnalogInput_Int | null), 4>
}

export interface Productivity1000_P1_04ADL_1 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_04ADL_1
    channels: FixedLengthArray<(Productivity1000_AnalogInput_Int | null), 4>
}

export interface Productivity1000_P1_08ADL_1 extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_08ADL_1
    channels: FixedLengthArray<(Productivity1000_AnalogInput_Int | null), 8>
}

export interface Productivity1000_P1_04RTD extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_04RTD
    channels: FixedLengthArray<(Productivity1000_AnalogInput_Float | null), 4>
}

export interface Productivity1000_P1_04THM extends BaseSubcomponent {
    subcomponentType: Productivity1000CardType.P1_04THM
    channels: FixedLengthArray<(Productivity1000_AnalogInput_Float | null), 4>
}

export interface BaseSubcomponentData {
    name: string;
    description: string;
    productImageUrl: string;
    datasheetUrl: string;
}


export enum Productivity1000ChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput_Float = "AnalogInput_Float",
    AnalogInput_Int = "AnalogInput_Int",
    AnalogOutput = "AnalogOutput",
}

export enum DurapulseChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
    EnumOutput = "EnumOutput",
    EnumInput = "EnumInput",
}

export enum SureServo2ChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
    EnumOutput = "EnumOutput",
    EnumInput = "EnumInput",
    PathConfig_Position = "PathConfig_Position",
    PathConfig_Speed = "PathConfig_Speed",
}

export enum TLB4ModbusTCPChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
    Settings = "Settings",
}

export enum CR011RChannelType {
    DMXOutput = "DMXOutput",
    AnalogOutput = "AnalogOutput",
}

export enum IoLinkChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
}

export enum DX200ChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
}

export enum YRC1000ChannelType {
    DigitalInput = "DigitalInput",
    DigitalOutput = "DigitalOutput",
    AnalogInput = "AnalogInput",
    AnalogOutput = "AnalogOutput",
}

export interface Productivity1000CardData extends BaseSubcomponentData {
    channelTypes: Productivity1000ChannelType[];
}



export const SubcomponentData: Record<SubcomponentType, Productivity1000CardData> = {
    [Productivity1000CardType.P1_15TD2]: {
        name: "P1-15TD2 Digital Output Module",
        description: "15-Channel 24VDC Sink/Source Output Module, 1A/pt",
        productImageUrl: "/assets/images/productivity-1000/p1-15td2.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-15td2.pdf",
        channelTypes: [
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
        ]
    },
    [Productivity1000CardType.P1_16ND3]: {
        name: "P1-16ND3 Digital Input Module",
        description: "16-Channel 24VDC Digital Input Module",
        productImageUrl: "/assets/images/productivity-1000/p1-16nd3.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-16nd3.pdf",
        channelTypes: [
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
        ]
    },
    [Productivity1000CardType.P1_08NA]: {
        name: "P1-08NA Digital Input Module",
        description: "8-Channel 120VAC Digital Input Module",
        productImageUrl: "/assets/images/productivity-1000/p1-08na.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-08na.pdf",
        channelTypes: [
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
        ]
    },
    [Productivity1000CardType.P1_15CDD2]: {
        name: "P1-15CDD2 Digital Combo Module",
        description: "15-Channel 24VDC Digital Input/Output Module",
        productImageUrl: "/assets/images/productivity-1000/p1-15cdd2.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-15cdd2.pdf",
        channelTypes: [
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalInput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
            Productivity1000ChannelType.DigitalOutput,
        ]
    },
    [Productivity1000CardType.P1_04DAL_1]: {
        name: "P1-04DAL-1 Analog Output Module",
        description: "4-Channel Analog Output Module, 4-20 mA, 12-bit",
        productImageUrl: "/assets/images/productivity-1000/p1-04dal-1.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-04dal-1.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
        ]
    },
    [Productivity1000CardType.P1_08DAL_1]: {
        name: "P1-08DAL-1 Analog Output Module",
        description: "8-Channel Analog Output Module, 4-20 mA, 12-bit",
        productImageUrl: "/assets/images/productivity-1000/p1-08dal-1.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-08dal-1.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
            Productivity1000ChannelType.AnalogOutput,
        ]
    },
    [Productivity1000CardType.P1_04AD_1]: {
        name: "P1-04AD-1 Analog Input Module",
        description: "4-Channel Analog Input Module, 4-20 mA, 16-bit",
        productImageUrl: "/assets/images/productivity-1000/p1-04ad-1.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-04ad-1.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
        ]
    },
    [Productivity1000CardType.P1_04ADL_1]: {
        name: "P1-04ADL-1 Analog Input Module",
        description: "4-Channel Analog Input Module, 4-20 mA, 13-bit",
        productImageUrl: "/assets/images/productivity-1000/p1-04adl-1.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-04adl-1.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
        ]
    },
    [Productivity1000CardType.P1_08ADL_1]: {
        name: "P1-08ADL-1 Analog Input Module",
        description: "8-Channel Analog Input Module, 4-20 mA, 13-bit",
        productImageUrl: "/assets/images/productivity-1000/p1-08adl-1.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-08adl-1.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
            Productivity1000ChannelType.AnalogInput_Int,
        ]
    },
    [Productivity1000CardType.P1_04RTD]: {
        name: "P1-04RTD RTD Input Module",
        description: "4-Channel RTD Input Module",
        productImageUrl: "/assets/images/productivity-1000/p1-04rtd.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-04rtd.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
        ]
    },
    [Productivity1000CardType.P1_04THM]: {
        name: "P1-04THM Thermocouple Input Module",
        description: "4-Channel Thermocouple Input Module",
        productImageUrl: "/assets/images/productivity-1000/p1-04thm.jpg",
        datasheetUrl: "/assets/datasheets/productivity-1000/p1-04thm.pdf",
        channelTypes: [
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
            Productivity1000ChannelType.AnalogInput_Float,
        ]
    }


}


/////////////// Channels ///////////////

export type ChannelOverride<T extends Channel, Overrides extends Partial<Pick<T, keyof T>>> = Omit<T, keyof Overrides> & Overrides;

export interface Channel extends CoreConfigData {
    units: string | null;
}

export type Productivity1000Channel =
    Productivity1000_DigitalInput |
    Productivity1000_DigitalOutput |
    Productivity1000_AnalogInput_Int |
    Productivity1000_AnalogInput_Float |
    Productivity1000_AnalogOutput;


export interface Productivity1000_DigitalInput extends Channel {
    channelType: Productivity1000ChannelType.DigitalInput;
    samplePeriod: number;

}

export interface Productivity1000_DigitalOutput extends Channel {
    channelType: Productivity1000ChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface Productivity1000_AnalogInput_Int extends Channel {
    channelType: Productivity1000ChannelType.AnalogInput_Int;
    samplePeriod: number;
    slope: number;
    intercept: number;
}

export interface Productivity1000_AnalogInput_Float extends Channel {
    channelType: Productivity1000ChannelType.AnalogInput_Float;
    samplePeriod: number;
    slope: number;
    intercept: number;
}

export interface Productivity1000_AnalogOutput extends Channel {
    channelType: Productivity1000ChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
    slope: number;
    intercept: number;
}

export interface Durapulse_DigitalInput extends Channel {
    channelType: DurapulseChannelType.DigitalInput;
}

export interface Durapulse_DigitalOutput extends Channel {
    channelType: DurapulseChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface Durapulse_AnalogInput extends Channel {
    channelType: DurapulseChannelType.AnalogInput;
}

export interface Durapulse_AnalogOutput extends Channel {
    channelType: DurapulseChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}

export interface Durapulse_EnumOutput<T> extends Channel {
    channelType: DurapulseChannelType.EnumOutput;
    initialValue: T;
    options: T[];
}

export interface Durapulse_EnumInput<T> extends Channel {
    channelType: DurapulseChannelType.EnumInput;
    options: T[];
}

export interface TLB4ModbusTCP_DigitalInput extends Channel {
    channelType: TLB4ModbusTCPChannelType.DigitalInput;
}

export interface TLB4ModbusTCP_DigitalOutput extends Channel {
    channelType: TLB4ModbusTCPChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface TLB4ModbusTCP_AnalogInput extends Channel {
    channelType: TLB4ModbusTCPChannelType.AnalogInput;
}

export interface TLB4ModbusTCP_AnalogOutput extends Channel {
    channelType: TLB4ModbusTCPChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}

export enum TLB4ModbusTCPUnit {
    Kilograms = "Kilograms",
    Pounds = "Pounds"
}

export interface TLB4ModbusTCPSettings {
    fullScale: number,
    unit: TLB4ModbusTCPUnit,
}

export interface TLB4ModbusTCP_Settings extends Channel {
    channelType: TLB4ModbusTCPChannelType.Settings;
    settings: TLB4ModbusTCPSettings
}

export interface CR011R_DMXOutput extends Channel {
    channelType: CR011RChannelType.DMXOutput,
    initialValue: number[];
}

export interface CR011R_AnalogOutput extends Channel {
    channelType: CR011RChannelType.AnalogOutput,
    initialValue: number,
}

export interface SureServo2_DigitalInput extends Channel {
    channelType: SureServo2ChannelType.DigitalInput;
}

export interface SureServo2_DigitalOutput extends Channel {
    channelType: SureServo2ChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface SureServo2_AnalogInput extends Channel {
    channelType: SureServo2ChannelType.AnalogInput;
}

export interface SureServo2_AnalogOutput extends Channel {
    channelType: SureServo2ChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}

export interface SureServo2_EnumOutput<T> extends Channel {
    channelType: SureServo2ChannelType.EnumOutput;
    initialValue: T;
    options: T[];
}

export interface SureServo2_EnumInput<T> extends Channel {
    channelType: SureServo2ChannelType.EnumInput;
    options: T[];
}

export enum SureServo2PathPositionCmdType {
    ABSOLUTE = "ABSOLUTE",
    RELATIVE = "RELATIVE",
    INCREMENTAL = "INCREMENTAL",
}


export interface SureServo2PathConfig extends Channel {
    accelerationTime_ms: number; // The number of milliseconds to accelerate to 3000 rpm (idk why they configure acceleration in this manner on the drive...)
    decelerationTime_ms: number;// The number of milliseconds to accelerate to 3000 rpm (idk why they configure acceleration in this manner on the drive...)
}

export interface SureServo2PathConfig_Position extends SureServo2PathConfig {
    type: SureServo2PathType.POSITION;
    targetSpeed: number | null; // only for POSITION paths
    positionCmdType: SureServo2PathPositionCmdType | null; // only for POSITION paths
    targetPosition: number; // The target position in PUU
}

export interface SureServo2PathConfig_Speed extends SureServo2PathConfig {
    type: SureServo2PathType.SPEED;
    targetSpeed: number; // The target speed in rpms
}


export interface DX200_DigitalInput extends Channel {
    channelType: DX200ChannelType.DigitalInput;
}

export interface DX200_DigitalOutput extends Channel {
    channelType: DX200ChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface DX200_AnalogInput extends Channel {
    channelType: DX200ChannelType.AnalogInput;
}

export interface DX200_AnalogOutput extends Channel {
    channelType: DX200ChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}


export interface IoLink_DigitalInput extends Channel {
    channelType: IoLinkChannelType.DigitalInput;
}

export interface IoLink_DigitalOutput extends Channel {
    channelType: IoLinkChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface IoLink_AnalogInput extends Channel {
    channelType: IoLinkChannelType.AnalogInput;
}

export interface IoLink_AnalogOutput extends Channel {
    channelType: IoLinkChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}


export type YRC1000Channel =
    YRC1000_DigitalInput |
    YRC1000_DigitalOutput |
    YRC1000_AnalogInput |
    YRC1000_AnalogOutput;


export interface YRC1000UserChannels {
    digitalInputs: YRC1000_DigitalInput[];
    digitalOutputs: YRC1000_DigitalOutput[];
    analogInputs: YRC1000_AnalogInput[];
    analogOutputs: YRC1000_AnalogOutput[];
}

export interface YRC1000_DigitalInput extends Channel {
    channelType: YRC1000ChannelType.DigitalInput;
}

export interface YRC1000_DigitalOutput extends Channel {
    channelType: YRC1000ChannelType.DigitalOutput;
    initialValue: boolean;
}

export interface YRC1000_AnalogInput extends Channel {
    channelType: YRC1000ChannelType.AnalogInput;
}

export interface YRC1000_AnalogOutput extends Channel {
    channelType: YRC1000ChannelType.AnalogOutput;
    minValue: number;
    maxValue: number;
    initialValue: number;
}